import { useEffect, useState } from "react";
import { Landmark, ShieldCheck, ShieldAlert, Clock } from "lucide-react";
import { toast } from "sonner";
import api, { fmtErr } from "../../lib/api";

const inputCls = "w-full rounded-xl border border-slate-200 px-3.5 py-2.5 text-sm text-shipany-navy placeholder:text-slate-400 focus:border-shipany-blue focus:outline-none focus:ring-2 focus:ring-shipany-blue/15";

export default function FranchiseBank() {
  const [bank, setBank] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ account_holder: "", bank_name: "", account_number: "", ifsc: "", account_type: "savings" });

  useEffect(() => {
    api.get("/franchise/bank-account").then(({ data }) => {
      setBank(data);
      if (data) setForm((f) => ({ ...f, account_holder: data.account_holder || "", bank_name: data.bank_name || "", ifsc: data.ifsc || "", account_type: data.account_type || "savings" }));
      setLoaded(true);
    }).catch(() => setLoaded(true));
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.put("/franchise/bank-account", form);
      toast.success("Bank details saved — pending verification by ShipAny");
      const { data } = await api.get("/franchise/bank-account");
      setBank(data);
    } catch (err) {
      toast.error(fmtErr(err));
    } finally {
      setBusy(false);
    }
  };

  if (!loaded) return null;
  const vs = bank?.verification_status;

  return (
    <div className="mx-auto max-w-2xl space-y-5" data-testid="franchise-bank-page">
      <div>
        <h1 className="font-display text-2xl font-bold text-shipany-navy">Payout Bank Account</h1>
        <p className="text-sm text-slate-500">Withdrawals are paid to this account after verification.</p>
      </div>

      {bank && (
        <div className={`flex items-center gap-3 rounded-2xl border p-4 ${vs === "verified" ? "border-emerald-200 bg-emerald-50" : vs === "rejected" ? "border-red-200 bg-red-50" : "border-amber-200 bg-amber-50"}`} data-testid="bank-status-card">
          {vs === "verified" ? <ShieldCheck className="h-5 w-5 text-emerald-600" /> : vs === "rejected" ? <ShieldAlert className="h-5 w-5 text-red-500" /> : <Clock className="h-5 w-5 text-amber-600" />}
          <div className="text-sm">
            <p className="font-bold capitalize">{vs}</p>
            <p className="text-xs opacity-80">{bank.account_number_masked} · {bank.ifsc} · {bank.bank_name}</p>
          </div>
        </div>
      )}

      <form onSubmit={submit} className="space-y-4 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
        <div className="flex items-center gap-2 text-sm font-bold text-shipany-navy"><Landmark className="h-4 w-4 text-shipany-blue" /> Bank Details</div>
        <div><label className="mb-1 block text-xs font-semibold text-slate-500">Account holder name *</label><input required value={form.account_holder} onChange={(e) => setForm({ ...form, account_holder: e.target.value })} data-testid="bank-holder-input" className={inputCls} /></div>
        <div><label className="mb-1 block text-xs font-semibold text-slate-500">Bank name *</label><input required value={form.bank_name} onChange={(e) => setForm({ ...form, bank_name: e.target.value })} data-testid="bank-name-input" className={inputCls} /></div>
        <div className="grid gap-4 sm:grid-cols-2">
          <div><label className="mb-1 block text-xs font-semibold text-slate-500">Account number *</label><input required inputMode="numeric" value={form.account_number} onChange={(e) => setForm({ ...form, account_number: e.target.value.replace(/\D/g, "") })} data-testid="bank-account-input" className={inputCls} placeholder={bank ? bank.account_number_masked : ""} /></div>
          <div><label className="mb-1 block text-xs font-semibold text-slate-500">IFSC *</label><input required maxLength={11} value={form.ifsc} onChange={(e) => setForm({ ...form, ifsc: e.target.value.toUpperCase() })} data-testid="bank-ifsc-input" className={inputCls} /></div>
        </div>
        <div>
          <label className="mb-1 block text-xs font-semibold text-slate-500">Account type</label>
          <select value={form.account_type} onChange={(e) => setForm({ ...form, account_type: e.target.value })} data-testid="bank-type-select" className={inputCls}>
            <option value="savings">Savings</option><option value="current">Current</option>
          </select>
        </div>
        <button disabled={busy} data-testid="bank-save-btn" className="w-full rounded-xl bg-shipany-blue py-2.5 text-sm font-bold text-white disabled:opacity-60">
          {busy ? "Saving…" : bank ? "Update Bank Account" : "Save Bank Account"}
        </button>
        <p className="text-[11px] text-slate-400">Any change re-triggers verification. Your full account number is never displayed after saving.</p>
      </form>
    </div>
  );
}
